import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { TechDetails } from "@/components/TechDetails";

import { IMAGES, BBN_SECURITY } from "@/lib/images";
import { SakerhetsfilmShowcase } from "@/components/sections/SakerhetsfilmShowcase";
import { CallbackRail } from "@/components/CallbackRail";

export const COMBO_FAQS: FaqItem[] = [
  {
    q: "Är kombifilm sämre på solskydd än ren solfilm?",
    a: "Nej. 3M:s kombiserie använder samma spektralt selektiva skikt som rena solfilmer, men har ett extra säkerhetsskikt under. Värmereduktionen ligger på 70–80 % beroende på toning, vilket är jämförbart med rena solfilmer i samma kategori.",
  },
  {
    q: "Är kombifilm sämre på inbrottsskydd än ren säkerhetsfilm?",
    a: "I lägre tjocklekar (100–175 μm) ligger skyddet på samma nivå. För maximal inbrottsfördröjning (300+ μm) rekommenderar vi fortfarande ren Ultra-säkerhetsfilm, eftersom solfilmsskiktet då adderar onödig tjocklek. Kombifilm är optimerad för mellansegmentet där både sol- och säkerhetsproblem finns samtidigt.",
  },
  {
    q: "När är kombifilm rätt val?",
    a: "När både värmeproblem och säkerhetsbehov finns på samma glasyta, till exempel butiksskyltfönster mot söder, advokatbyråers exponerade kontor, eller bilhallars showroom-glas. Du får båda funktionerna i samma installation och garanti, till lägre kostnad än två separata filmer.",
  },
  {
    q: "Vad blir kostnaden jämfört med två separata filmer?",
    a: "En kombifilm kostar typiskt 20–30 % mer per m² än enbart solfilm, men 30–40 % mindre än att installera solfilm + säkerhetsfilm separat. Räknat på hela installationen sparar ni både material- och arbetstidskostnad.",
  },
];

export const CLEAR_FAQS: FaqItem[] = [
  {
    q: "När väljs klar säkerhetsfilm framför tonad?",
    a: "I tre vanliga situationer: (1) glaset har redan solfilm från tidigare installation eller från fabriken (energiglas, lågemissionsglas), (2) glaset har frostad eller dekorerad folie för insynsskydd, eller (3) ni vill bevara fasadens helt orörda utseende, till exempel i kulturhistoriska byggnader och butiker där skyltfönstret ska vara helt klart.",
  },
  {
    q: "Försämrar klar film glasets prestanda på något sätt?",
    a: "Knappast märkbart. Visuell ljustransmission ligger på >88 % och spegelreflektans är minimal. Glaset ser helt orört ut från både insidan och utsidan, men beter sig som säkerhetsglas vid slag och splitter.",
  },
  {
    q: "Kan vi sätta klar säkerhetsfilm efteråt på glas som redan har solfilm?",
    a: "Ja. Den klara säkerhetsfilmen monteras invändigt direkt mot glaset. Solfilmen sitter på utsidan av samma glas (eller på insidan av andra glaset i isolerrutan). Båda funktionerna behålls. Vi gör alltid en kontroll så att existerande film inte avlöser sig.",
  },
  {
    q: "Vad blir prisskillnaden mot tonad säkerhetsfilm?",
    a: "Klar säkerhetsfilm är typiskt 10–15 % billigare per m² än motsvarande tonad/solreducerande version, eftersom den saknar spektralt selektivt skikt. Effektivt skydd och optik är likvärdig.",
  },
];

export const FAQS: FaqItem[] = [
  {
    q: "Vad gör säkerhetsfilm faktiskt — håller den emot inbrott?",
    a: "Säkerhetsfilm är en kraftig polyesterfilm (typiskt 100–375 mikron) som limmas mot insidan av glaset. När glaset krossas — av slag, sten eller verktyg — håller filmen ihop splittret som en sammanhängande matta. Inkräktaren får inte hål i glaset och kan inte sticka in en hand för att låsa upp eller klättra in. I praktiken förlängs inbrottstiden från sekunder till flera minuter, vilket är tillräckligt för att larm hinner aktivera och de flesta tjuvar bryter försöket.",
  },
  {
    q: "Vilken klass av säkerhetsfilm behöver vi?",
    a: "Det beror på syftet. För standard splitterskydd (vid stormrutor, fall- eller stötrisk) räcker 100 μm. För grundläggande inbrottsfördröjning rekommenderar vi 175–200 μm. För butikspartier, juvelerare, vapenförvaring och miljöer med dokumenterad rånrisk används 300–375 μm, ofta i kombination med stålram-förstärkning runt karmen. Vi går igenom hotbilden i rådgivningen och rekommenderar lägsta klass som ger tillräckligt skydd.",
  },
  {
    q: "Påverkar säkerhetsfilm dagsljus och utsikt?",
    a: "Nej. Klar säkerhetsfilm är optiskt nästan osynlig och släpper igenom >88 % av det synliga ljuset. Utomstående ser inte att filmen finns där — vilket också är en del av poängen. Det går också att kombinera med solfilm eller en tonad film om både splitterskydd och solskydd behövs.",
  },
  {
    q: "Kan säkerhetsfilm monteras på alla typer av glas?",
    a: "Ja, både enkelglas, 2-glas, 3-glas och laminerat glas. För isolerglas följer vi tillverkarens anvisningar för termisk belastning. Vi gör alltid en glasanalys innan installation så att garantin för glaset bibehålls.",
  },
  {
    q: "Sänker säkerhetsfilm försäkringspremien?",
    a: "Många försäkringsbolag ger rabatt eller mildare villkor på kommersiella objekt när dokumenterad säkerhetsfilm är installerad — särskilt inom handel, läkemedel, optik och juveler. Vi tillhandahåller installationsprotokoll med specifikation av film, klassning och installationsdatum som du kan skicka in till ditt försäkringsbolag.",
  },
  {
    q: "Vad är skillnaden mot laminerat säkerhetsglas?",
    a: "Laminerat säkerhetsglas (t.ex. P4A-glas) ger likvärdigt eller bättre skydd men kräver glasbyte med tillhörande karmarbete, kostnad och stillestånd. Säkerhetsfilm installeras på befintligt glas på några timmar per fönster, ofta med kostnad i storleksordningen 10–25 % av ett glasbyte. För eftermarknadsmontering är film därför nästan alltid den ekonomiskt bästa lösningen.",
  },
  {
    q: "Hur kombineras säkerhetsfilm med larm och kameror?",
    a: "Säkerhetsfilm är inte ett substitut för larm — det är ett komplement som ger tiden larmet behöver för att vara meningsfullt. Utan film tar inbrott ofta under en minut; med film tar samma försök 3–10 minuter, vilket är längre än polisens normala utryckningstid i de flesta tätorter. Vi samarbetar med flera larmoperatörer och kan koordinera installation om ni vill göra båda samtidigt.",
  },
  {
    q: "Hur länge håller säkerhetsfilm?",
    a: "Invändig säkerhetsfilm håller normalt 15–20 år utan att tappa funktion. Vi lämnar 10 års produktgaranti och 10 års arbetsgaranti (upp till 15 års produktgaranti på 3M Ultra-serien). Filmen behöver inget underhåll utöver normal fönsterputs.",
  },
  {
    q: "Vad gör 3M Ultra annorlunda jämfört med vanlig säkerhetsfilm?",
    a: "Traditionell säkerhetsfilm är en enda tjock polyesterfilm. 3M Ultra är uppbyggd av upp till 42 mikrotunna polymerlager som lamineras till en kompositstruktur. Resultatet är en film som är tunnare än konventionell film men ändå har upp till 5 gånger högre rivhållfasthet — den 'studsar tillbaka' istället för att gå sönder. Ultra S800 är godkänd enligt EN 356 och båda klarar EN 12600.",
  },
  {
    q: "Behöver Ultra-filmen kompletteras med ramförstärkning?",
    a: "För högsta klassen av inbrottsskydd kombineras Ultra med 3M Impact Protection Attachment — en lim- eller mekanisk infästning som binder filmen till karmen. Då hålls hela glaspartiet kvar i ramen även om glaset spricker, vilket ger den allra längsta fördröjningen vid inbrottsförsök.",
  },
  {
    q: "När väljer jag 3M Safety-serien framför Ultra?",
    a: "Safety S40, S70 och S140 är 3M:s prisvärdare alternativ — fortfarande optiskt klara och UV-blockerande, men utan Ultras flerlagerteknik. De passar när du vill fördröja intrång med 3M-film men inte behöver Ultras styrka. S140 är dock en av 3M:s kraftigaste filmer och håller en mycket hög nivå av inbrottsfördröjning.",
  },
  {
    q: "Vad är skillnaden på invändig och utvändig säkerhetsfilm?",
    a: "Invändig film (vanligast) skyddar mot inbrott genom att hålla ihop glaset när det krossas utifrån. Utvändig film (t.ex. 3M Safety S40/S70 Exterior eller Solar Screen Safe 4XC) fungerar som ett offerlager mot klotter, repor och vandalism — och som splitterskydd. Vid grov skadegörelse byts filmen i stället för hela rutan.",
  },
];


export const Route = createFileRoute("/sakerhetsfilm/")({
  head: () => ({
    meta: [
      { title: "Säkerhetsfilm företag, kontor, butik & fastighet — 3M Ultra | Offert | BBN Nordic" },
      { name: "description", content: "Säkerhetsfilm för företag, kontor, butik och fastighet — inbrottsskydd fönster, splitterskyddsfilm och skyddsfilm för glas enligt EN 12600. 3M Ultra, UV-skyddsfilm och Solar Screen. Auktoriserad installatör med 10 års garanti — snabb offert och montering utan glasbyte." },
      { name: "keywords", content: "säkerhetsfilm, säkerhetsfilm företag, säkerhetsfilm kontor, säkerhetsfilm fastighet, säkerhetsfilm butik, säkerhetsfilm fönster, säkerhetsfilm montering, säkerhetsfilm offert, säkerhetsfilm projekt, 3m säkerhetsfilm, skyddsfilm, skyddsfilm glas, skyddsfilm fönster, splitterskydd fönster, splitterskyddsfilm, splitterskyddsfilm fastighet, inbrottsskydd fönster, inbrottsskydd glas företag, uv skyddsfilm, säkerhetsfilm till fastighet, säkerhetsfilm för fönster" },
      { property: "og:title", content: "Säkerhetsfilm företag, kontor & fastighet — 3M Ultra" },
      { property: "og:description", content: "Inbrottsskydd fönster och splitterskyddsfilm enligt EN 12600 — säkerhetsfilm för företag, kontor, butik och fastighet. Auktoriserad 3M Ultra-installatör." },
      { property: "og:url", content: "https://bbnnordic.se/sakerhetsfilm" },
      { property: "og:image", content: IMAGES.sakerhetsfilm },

    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/sakerhetsfilm" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Installation av säkerhetsfilm för glas",
          serviceType: "Säkerhetsfilms-installation",
          provider: { "@type": "LocalBusiness", name: "BBN Nordic AB", url: "/" },
          areaServed: [
            { "@type": "AdministrativeArea", name: "Skåne" },
            { "@type": "City", name: "Göteborg" },
          ],
          image: IMAGES.sakerhetsfilm,
          url: "/sakerhetsfilm",
          brand: { "@type": "Brand", name: "3M" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Säkerhetsfilm för glas",
          category: "Fönsterfilm / Säkerhetsfilm",
          description: "3M och Solar Screen säkerhetsfilm som förstärker glaset mot inbrott, splitter och vandalism. Auktoriserad installation i Skåne.",
          image: IMAGES.sakerhetsfilm,
          brand: { "@type": "Brand", name: "3M" },
          manufacturer: { "@type": "Organization", name: "3M" },
          url: "/sakerhetsfilm",
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "SEK",
            lowPrice: "8000",
            priceSpecification: { "@type": "PriceSpecification", price: "8000", priceCurrency: "SEK" },
            availability: "https://schema.org/InStock",
            seller: { "@id": "/#organization" },
            areaServed: "SE",
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: "SE",
              returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "SEK" },
              shippingDestination: { "@type": "DefinedRegion", addressCountry: "SE" },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 5, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 14, unitCode: "DAY" },
              },
            },
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "6",
            bestRating: "5",
            worstRating: "1",
          },
          review: [
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Andrée Johnsson" },
              reviewBody: "Kan varmt rekommenderas. Vi hade en undermålig solfilm som konkurrent satt upp, Marcus knackade på när han var hos grannen och sa att det där kan göras bättre.",
            },
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Christian Backe" },
              reviewBody: "Vi hade problem med att våra fönster släppte in väldigt mycket värme och valde en film som BBN satte med bästa resultat. Kan varmt rekommendera!",
            },
          ],
        }),

      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqJsonLd([...FAQS, ...COMBO_FAQS, ...CLEAR_FAQS])),
      },
    ],
  }),
  component: () => (
    <ContentPage
      disableCta
      eyebrow="TJÄNST"
      title="Säkerhetsfilm för glas"
      subtitle="Skyddsfilm för fönster håller ihop glaset vid inbrott, olyckor och vandalism. Vi monterar på befintliga rutor, utan glasbyte eller stillestånd, och dokumenterar skyddet."
      heroImage={IMAGES.sakerhetsfilmHero}
      breadcrumbs={[{ label: "Säkerhetsfilm" }]}
      sections={[
        {
          eyebrow: "VAD NI FÅR",
          heading: "Inbrott tar minuter istället för sekunder.",
          paragraphs: [
            "Skyddsfilm för fönster och säkerhetsfilm är två namn på samma produkt. Det är en kraftig film på glasets insida som håller kvar splittret vid slag, krossförsök och vandalism. Den fördröjer intrång och skyddar personal, kunder och varor mot glassplitter.",
            "Utan film kan en sten eller skruvmejsel skapa ett hål i rutan på under en minut. Följden kan bli stulna varor för ett par tusen och en verksamhet som inte kan öppna som planerat. Med säkerhetsfilm krossas glaset fortfarande, men skärvorna hålls ihop som en sammanhängande matta. Inkräktaren får inte hål för att sticka in handen och låsa upp, och försöket avbryts innan larmet hunnit göra sitt jobb.",
            "För dig innebär det mindre risk för avbrott i verksamheten och att personalen skyddas från flygande glasskärvor vid olyckor eller hot. En dokumenterad installation ger också ofta lägre försäkringspremie. Vi monterar filmen på befintligt glas på några timmar per fönster.",
          ],
          bullets: [
            "Inbrottsförsök tar 3–10 minuter — tillräckligt för larm och utryckning",
            "Personal och kunder skyddas från glassplitter vid olycka eller hot",
            "Lägre risk för verksamhetsavbrott efter inbrott eller skadegörelse",
            "Försäkringspremier sänks ofta vid dokumenterad installation",
            "Optiskt nästan osynlig — fasaden och dagsljuset orört",
          ],
          facts: [
            { value: "3–10 min", label: "Inbrottstid med film", source: "vs. <60 sek utan film" },
            { value: "EN 12600", label: "Säkerhetsglas-standard", source: "Pendelslagstest, upp till 2B2" },
            { value: "EN 356", label: "Inbrottsskydd", source: "Upp till P2A med 3M Ultra S800" },
          ],
          extra: (
            <div className="space-y-6">
              <TechDetails
                summary="Teknisk certifiering & klassning — fullständig översikt"
                sources={[
                  { label: "Tekniska datablad för 3M säkerhetsfilm — BBN Nordic", href: "/datablad" },
                  { label: "SS-EN 12600 — Glas i byggnader, pendelslagstest (SIS)", href: "https://www.sis.se/produkter/byggnadsmaterial-och-byggnader/glas/ssen126002003/" },
                  { label: "SS-EN 356 — Manuellt angrepp (SIS)", href: "https://www.sis.se/produkter/byggnadsmaterial-och-byggnader/glas/ssen3562000/" },
                ]}
              >
                <p>
                  Beroende på glasets konstruktion och vald film uppfyller lösningen
                  klassningar upp till <strong>2B2 enligt EN 12600</strong>
                  (pendelslagstest för säkerhetsglas) och <strong>P2A enligt EN 356</strong>
                  (manuellt angrepp / inbrottsskydd). Detta är de europeiska standarder
                  svenska försäkringsbolag utgår från när de bedömer skyddsnivå.
                </p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><strong>EN 12600</strong> — pendelslagstest för säkerhetsglas (upp till klass 2B2)</li>
                  <li><strong>EN 356</strong> — manuellt angrepp / inbrottsskydd (upp till P2A med 3M Ultra S800)</li>
                  <li><strong>ASTM E84</strong> — brandklass A för 3M Ultra S600/S800</li>
                  <li><strong>ANSI Z97.1</strong> — amerikansk standard för säkerhetsglas</li>
                  <li><strong>CE-märkning</strong> — fullständig dokumentation tillhandahålls på begäran</li>
                </ul>
                <p>
                  Installationsprotokoll med filmklass, monteringsdatum och CE-deklaration
                  levereras digitalt efter slutbesiktning — direkt användbart som underlag
                  till försäkringsbolag.
                </p>
              </TechDetails>
            </div>
          ),
          image: BBN_SECURITY[0],
        },
        {
          eyebrow: "ANVÄNDNINGSOMRÅDEN",
          heading: "Här använder våra kunder säkerhetsfilm.",
          paragraphs: [
            "Vi installerar säkerhetsfilm både där det redan finns en hotbild och där ett enda inbrott skulle få stora följder. Här är några vanliga användningsområden:",
          ],
          bullets: [
            "Butiker med exponerade varor — kläder, elektronik, optik, sport, smycken",
            "Kontor i markplan med utsatta entrépartier",
            "Skolor och förskolor — splitterskydd vid sport- eller lekrelaterad krossning",
            "Vårdcentraler & receptioner — splitterskydd vid hotsituationer",
            "Livsmedelsbutiker och storkök — hygien, splitterskydd och skydd av inredning",
            "Ambassader, myndigheter och skyddsobjekt — förhöjd hotbild och krav på fasadbevarande",
            "Bostadsrättsföreningar — markplan och balkongdörrar mot innergård",
            "Villor — terrassdörrar och fönster i baksida som inte syns från gatan",
            "Industri & lager — entré- och kontorspartier mot allmän plats",
            "Bilhallar med showroom-fönster mot gatan",
          ],
          image: BBN_SECURITY[1],
        },
        {
          eyebrow: "FILMTJOCKLEK",
          heading: "Rätt klass för rätt hotbild.",
          paragraphs: [
            "Vi väljer säkerhetsfilm efter vilket skydd du behöver, vad glaset klarar och vad det får kosta. Tjockare film är inte alltid bättre. På fel glas kan den skapa termisk stress. De här tjocklekarna används för olika behov:",
          ],
          bullets: [
            "100 μm — standard splitterskydd, splitter hålls ihop vid krossning",
            "175–200 μm — fördröjt inbrottsskydd för kontor, mindre butik, hem",
            "300 μm — handelsbutik med varor i utsatt läge, vandalism-risk",
            "375 μm (3M Ultra S800) — högsta klassen, juvelerare, optik, vapenförvaring",
            "Kombineras vid behov med 3M Impact Protection Attachment — limmar filmen mot karmen för ännu längre fördröjning",
          ],
          image: BBN_SECURITY[2],
        },
        {
          eyebrow: "KOMBILÖSNING",
          heading: "Säkerhetsfilm med solfilm — två funktioner i en.",
          paragraphs: [
            "Med 3M:s kombifilm får du säkerhetsfilmens hela skydd mot inbrott och splitter tillsammans med minskad solvärme, bländning och UV-strålning. Båda funktionerna ingår i samma installation och garanti.",
            "Det passar butiker med skyltfönster, kontor med stora glaspartier och offentliga miljöer som behöver både skydd och en behagligare temperatur.",
          ],
          bullets: [
            "Skydd mot inbrott och splitter",
            "Värmereduktion upp till 80 %",
            "UV-blockering 99 %",
            "En installation — full funktionalitet",
            "Lägre totalkostnad än två separata filmer",
          ],
          extra: (
            <div className="flex flex-wrap gap-3">
              <a href="/3m-sakerhetsfilm-med-solfilm" className="inline-flex items-center gap-2 rounded-full bg-gold text-navy px-5 py-3 text-sm font-semibold hover:bg-gold-bright transition-colors">
                Läs mer om kombifilm →
              </a>
              <a href="/datablad/3m-safety-40-invandig.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold hover:border-bbn-accent/40 transition-colors">
                Datablad Safety S40 (PDF)
              </a>
              <a href="/datablad/3m-safety-70.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold hover:border-bbn-accent/40 transition-colors">
                Datablad Safety S70 (PDF)
              </a>
            </div>
          ),
          image: BBN_SECURITY[3],
        },
        {
          eyebrow: "KLAR FILM",
          heading: "Klar säkerhetsfilm när du inte behöver solfilm.",
          paragraphs: [
            "Har du redan energiglas eller lågemissionsglas med solskydd kan klar säkerhetsfilm passa. Detsamma gäller om du har dekorfilm som insynsskydd. Säkerhetsfilmen är optiskt nästan osynlig och syns inte för den som ser fönstret utifrån.",
            "Glaset ser orört ut och beter sig som säkerhetsglas vid slag och splitter. Det passar skyltfönster där varorna ska synas tydligt och fasader med kulturhistoriska värden.",
          ],
          bullets: [
            "Ingen färgförändring av glaset — >88 % visuell ljustransmission",
            "Lämplig för butikers monterglas, vitriner och skyltfönster",
            "Kan adderas senare på befintligt glas med solfilm eller dekorfolie",
            "Lika hög skyddsnivå som tonad säkerhetsfilm",
            "Bevarar kulturhistoriska fasader och äldre arkitektur",
          ],
          extra: (
            <div className="flex flex-wrap gap-3">
              <a href="/3m-sakerhetsfilm-utan-solfilm" className="inline-flex items-center gap-2 rounded-full bg-gold text-navy px-5 py-3 text-sm font-semibold hover:bg-gold-bright transition-colors">
                Läs mer om klar säkerhetsfilm →
              </a>
              <a href="/datablad/3m-safety-s140.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold hover:border-bbn-accent/40 transition-colors">
                Datablad Safety S140 (PDF)
              </a>
            </div>
          ),
          image: BBN_SECURITY[4],
        },
        {
          eyebrow: "3M ULTRA S600 & S800",
          heading: "Så är 3M Ultra S600 och S800 uppbyggda.",
          paragraphs: [
            "3M Ultra S600 och S800 är 3M:s patenterade serie av säkerhetsfilmer med flerlagerteknik. Filmerna är uppbyggda av upp till 42 mikrotunna polymerlager som lamineras till en kompositstruktur — resultatet är en film som är tunnare än konventionell film men ändå upp till 5 gånger starkare i rivhållfasthet. Den 'studsar tillbaka' vid slag i stället för att rivas isär.",
            "Båda filmerna är optiskt klara och klarar kraven för säkerhetsglas enligt EN 12600 och är klass-A-klassade enligt ASTM E84. Ultra S800 är även godkänd enligt EN 356. Vid större projekt kan vi simulera tryckpåverkan från explosioner och göra riskbedömning per fasad. För maximal inbrottsfördröjning kombineras Ultra med 3M Impact Protection Attachment — en kraftig säkerhetsfog som binder filmen mot karmen så hela glaspartiet hålls kvar i ramen.",
          ],
          bullets: [
            "Patenterad säkerhetsfilm från 3M",
            "Upp till 42 polymerlager — extremt starka och flexibla",
            "Används mot inbrott, vandalism, olyckor och terror/explosionsskydd",
            "Uppfyller kraven för säkerhetsglas (EN 12600, ASTM E84 klass A)",
            "Ultra S800 är dessutom godkänd enligt EN 356",
            "Upp till 15 års produktgaranti + 10 års arbetsgaranti",
          ],
          extra: (
            <div className="flex flex-wrap gap-3">
              <a href="/3m-ultra-sakerhetsfilm" className="inline-flex items-center gap-2 rounded-full bg-gold text-navy px-5 py-3 text-sm font-semibold hover:bg-gold-bright transition-colors">
                Läs mer om 3M Ultra →
              </a>
              <a href="/datablad" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold hover:border-bbn-accent/40 transition-colors">
                Se alla datablad
              </a>
            </div>
          ),
          image: BBN_SECURITY[5],
        },
        {
          eyebrow: "3M SAFETY-SERIEN",
          heading: "S40, S70 & S140 — transparenta säkerhetsfilmer för olika nivåer.",
          paragraphs: [
            "3M Safety-serien kostar mindre än Ultra och har en enklare uppbyggnad. Tre tjocklekar täcker de flesta behov: S40 (4 mil), S70 (7 mil) och S140 (14 mil — en av 3M:s kraftigaste). Alla är optiskt klara och blockerar UV-strålar.",
            "Safety-serien används främst på butiks- och kontorsfönster där man vill fördröja intrång och få UV-skydd, men där Ultra-seriens prestanda inte krävs.",
          ],
          bullets: [
            "3M Safety S40 — 4 mil transparent, prisvärt splitterskydd och UV-blockering",
            "3M Safety S70 — 7 mil transparent, fördröjer intrång på butiks- och kontorsfönster",
            "3M Safety S140 — 14 mil, en av 3M:s kraftigaste filmer för väsentlig inbrottsfördröjning",
            "Samtliga är optiskt klara och påverkar inte ljusinsläppet",
            "Lämpliga för fönster där intrång ska fördröjas utan att glaset behöver bytas",
          ],
          image: BBN_SECURITY[6],
        },
        {
          eyebrow: "UTVÄNDIG MONTERING",
          heading: "Safety S40 & S70 Exterior — repskydd och säkerhet i ett.",
          paragraphs: [
            "3M:s utvändiga säkerhetsfilmer monteras på glasets utsida och fungerar både som säkerhetsfilm och som offerlager mot repor, klotter och annan yttre påverkan. Vid grov skadegörelse byts filmen — inte hela rutan.",
            "Den utvändiga monteringen är särskilt populär i butiksgator, kollektivtrafikmiljöer och offentliga ytor där repor och klotter är vanliga problem.",
          ],
          bullets: [
            "3M Safety S40 Exterior — 4 mil utvändig, UV-skydd + reptålig beläggning",
            "3M Safety S70 Exterior — 7 mil utvändig, kraftigare inbrottsfördröjning + repskydd",
            "Glaset behöver inte bytas vid grov skadegörelse — filmen offras istället",
            "Skyddar mot klotter, repor och vandalism på butiks- och entréglas",
          ],
          image: BBN_SECURITY[7],
        },
        {
          eyebrow: "VÅR PROCESS",
          heading: "Från riskanalys till färdig installation.",
          bullets: [
            "1. Kostnadsfri rådgivning — vi går igenom hotbild och försäkringsvillkor",
            "2. Platsbesök — uppmätning, glasanalys och rekommenderad filmklass",
            "3. Fast offert med specifikation av film, klass och installationsdetaljer",
            "4. Installation av certifierade montörer enligt 3M:s installationsprotokoll",
            "5. Dokumentation — installationsprotokoll för försäkringsbolag levereras digitalt",
            "6. 10 års produktgaranti + 10 års arbetsgaranti (upp till 15 år produkt på Ultra-serien)",
          ],
          image: BBN_SECURITY[8],
        },
      ]}
      related={[
        { to: "/sakerhetsfilm/solar-screen", label: "Solar Screen Säkerhetsfilm", desc: "Europeiskt prisvärt alternativ." },
        { to: "/datablad", label: "Tekniska datablad", desc: "PDF-datablad för 3M Ultra, Safety och Solar Screen." },
      ]}
      priorityContent={<SakerhetsfilmShowcase />}
    >

      <FaqSection
        heading="Vanliga frågor om säkerhetsfilm."
        intro="Här svarar vi på frågor vi får om säkerhetsfilm, glas och montering."
        items={FAQS}
      />
      <CallbackRail product="Säkerhetsfilm" />
    </ContentPage>
  ),
});
